// MCHD Room Scheduler - Main Navigation Bar Component
// Modern sidebar navigation with MCHD branding and role-based access
// Modified: 08-10-2025 15:40:00 - Added collapsible/compact mode support (Phase 2)

import { useState } from 'react';
import { Group, Code, Box, Image, ActionIcon, Tooltip } from '@mantine/core';
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserRole } from '@mchd/shared/types';
import { MCHD_BRANDING, getNavigationSections } from '../../../config/navigation';
import { NavbarItem } from './NavbarItem';
import { usePlannerStore } from '../../../stores/plannerStore';
import classes from './Navbar.module.css';

/**
 * Props for the Navbar component
 */
interface NavbarProps {
  userRole: UserRole;
  onLogout: () => void;
  onNavigate?: (path: string) => void;
  /** Whether sidebar is in collapsed/compact mode */
  collapsed?: boolean;
}

/**
 * Main navigation bar component with MCHD branding
 * 
 * Enhanced with collapsible/compact mode for better space utilization (Phase 2)
 * 
 * @param userRole - Current user role for navigation filtering
 * @param onLogout - Logout handler function
 * @param onNavigate - Optional navigation handler
 * @param collapsed - Whether sidebar is in collapsed/compact mode
 * @returns JSX element
 */
export function Navbar({ userRole, onLogout, onNavigate, collapsed = false }: NavbarProps): JSX.Element {
  // Use React Router hooks with fallback
  let navigate: any = null;
  let location: any = null;
  
  try {
    navigate = useNavigate();
    location = useLocation();
  } catch (error) {
    // React Router not available, use fallback
    console.warn('React Router not available, using fallback navigation');
  }
  
  const [active, setActive] = useState<string>(location?.pathname || '/');
  const toggleSidebarCollapsed = usePlannerStore((state) => state.toggleSidebarCollapsed);

  /**
   * Handle navigation item click
   * @param link - Navigation link
   */
  const handleNavigation = (link: string): void => {
    setActive(link);
    
    if (link === '/logout') {
      onLogout();
      return;
    }
    
    if (onNavigate) {
      onNavigate(link);
    } else if (navigate) {
      navigate(link);
    } else {
      // Fallback: use window.location for navigation
      window.location.href = link;
    }
  };

  // Get navigation sections based on user role
  const navigationSections = getNavigationSections(userRole);

  // Separate main sections from account section
  const mainSections = navigationSections.filter(section => section.title !== 'Account');
  const accountSection = navigationSections.find(section => section.title === 'Account');

  // Render navigation sections
  const sectionElements = mainSections.map((section) => (
    <div key={section.title} className={classes.navigationSection}>
      <div className={classes.sectionHeader}>
        {section.title}
      </div>
      <div className={classes.sectionItems}>
        {section.items.map((item) => (
          <NavbarItem
            key={item.label}
            item={item}
            isActive={active === item.link}
            onClick={() => handleNavigation(item.link)}
          />
        ))}
      </div>
    </div>
  ));

  // Render account section items
  const accountLinks = accountSection?.items.map((item) => (
    <NavbarItem
      key={item.label}
      item={item}
      isActive={active === item.link}
      onClick={() => handleNavigation(item.link)}
    />
  ));

  return (
    <nav className={`${classes.navbar} ${collapsed ? classes.collapsed : ''}`}>
      {/* Main navigation content */}
      <div className={classes.navbarMain}>
        {/* Header with MCHD branding */}
        <Group className={classes.header} justify={collapsed ? "center" : "space-between"}>
          {!collapsed ? (
            <>
              <Group gap="sm">
                <Image
                  src={MCHD_BRANDING.logo}
                  alt="MCHD Logo"
                  height={32}
                  width="auto"
                  className={classes.logo}
                />
                <Box>
                  <div className={classes.brandName}>{MCHD_BRANDING.name}</div>
                  <div className={classes.brandDescription}>{MCHD_BRANDING.description}</div>
                </Box>
              </Group>
              <Code fw={700} className={classes.version}>
                {MCHD_BRANDING.version}
              </Code>
            </>
          ) : (
            <Image
              src={MCHD_BRANDING.logo}
              alt="MCHD Logo"
              height={32}
              width="auto"
              className={classes.logo}
            />
          )}
        </Group>

        {/* Collapse toggle button */}
        <Box className={classes.toggleButton}>
          <Tooltip label={collapsed ? "Expand Sidebar" : "Collapse Sidebar"} position="right">
            <ActionIcon
              onClick={toggleSidebarCollapsed}
              variant="subtle"
              color="white"
              size="md"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <IconLayoutSidebarLeftExpand size={20} /> : <IconLayoutSidebarLeftCollapse size={20} />}
            </ActionIcon>
          </Tooltip>
        </Box>

        {/* Navigation sections */}
        {sectionElements}
      </div>

      {/* Account navigation */}
      <div className={classes.footer}>
        {accountLinks}
      </div>
    </nav>
  );
}
